import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useMutation } from "@tanstack/react-query";
import {
  MRT_RowSelectionState,
  MRT_ToolbarAlertBanner as ToolBarAlertBanner,
} from "material-react-table";
import {
  Box,
  Button,
  Stack,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField,
  Tooltip,
} from "@mui/material";
import dayjs from "dayjs";

import { EsgDeleteIcon, EsgDownloadIcon } from "assets";
import {
  SiteDTO,
  downloadUtilityBills,
  getSavedSearchFilters,
  getSavedSearchFiltersList,
} from "api";
import {
  objectToColumnFilters,
  useBulkDeleteUtilityBills,
  useNotify,
  usePagination,
  useSites,
  useTableFilters,
  useUtilityBillsYears,
} from "hooks";
import {
  useSaveCurrentUrl,
  useSetBillEntriesNavigation,
  useSetDefaultUtilityBillsSelection,
  useUserRole,
} from "recoils";
import { downloadFile, extractErrorMessage, getDateFormatset } from "utils";
import { LOCAL_STORAGE_KEYS } from "consts";
import {
  ActiveFilters,
  CustomMaterialTable,
  DeleteDialog,
  EsgDatePickerField,
  InfoLabel,
  Loading,
  MultiSelectControlledOption,
  Pagination,
  Switch,
  useDialogControls,
  useTableSorting,
} from "components";
import { EsgMultiselectControlledField } from "components/inputs/esg-multi-select-field";

import { UTILITY_BILLS_TABLE } from "./consts";
import {
  buildBillsRecalculationDisplayFilters,
  getMuiTableBodyRowProps,
  pickBillsRecalculationQueryParams,
} from "./helpers";
import { filterParamsToQuery } from "./helpers/filterParamsToQuery";
import {
  createDocumentSummaryFilterConfig,
  DocumentSummaryFilters,
} from "./filter-config";
import {
  ExportButtonWrapper,
  getBillEntriesColums,
  injectFilterTextFieldProps,
  BillsRecalculationButton,
} from "./components";
import { useFetchBillsData } from "./hooks";

export const UtilityBillsPage = () => {
  // ---- Hooks
  const notify = useNotify();
  const navigate = useNavigate();
  const currentUrl = useLocation();
  const saveCurrentUrl = useSaveCurrentUrl();
  const setDefaultSelection = useSetDefaultUtilityBillsSelection();
  const setBillEntriesNavigation = useSetBillEntriesNavigation();
  const deleteDialogControls = useDialogControls();
  const userRole = useUserRole();
  const { data: availableYears, isLoading: loadingAvailableYears } =
    useUtilityBillsYears();
  const { data: sitesData } = useSites();

  const tableInstanceRef = useRef<any>(null);
  const rowVirtualizerInstanceRef = useRef<any>(null);
  const isUserPaginationChange = useRef<boolean>(false);

  // ---- Const
  // Local Storage
  const storedStartDate = localStorage.getItem(
    LOCAL_STORAGE_KEYS.DOCUMENT_SUMMARY_PAGE_START_DATE,
  );
  const storedEndDate = localStorage.getItem(
    LOCAL_STORAGE_KEYS.DOCUMENT_SUMMARY_PAGE_END_DATE,
  );
  const storedSitesFilter = JSON.parse(
    localStorage.getItem(
      LOCAL_STORAGE_KEYS.DOCUMENT_SUMMARY_PAGE_SITES_FILTER,
    ) || "[]",
  );
  const storedIsEdited =
    localStorage.getItem(LOCAL_STORAGE_KEYS.DOCUMENT_SUMMARY_PAGE_IS_EDITED) ===
    "true";
  const storedIsMyTask =
    localStorage.getItem(
      LOCAL_STORAGE_KEYS.DOCUMENT_SUMMARY_PAGE_IS_MY_TASK,
    ) === "true";
  const storedShowDuplicates =
    localStorage.getItem(
      LOCAL_STORAGE_KEYS.DOCUMENT_SUMMARY_PAGE_SHOW_DUPLICATES,
    ) === "true";
  const storedConsumptionStartDateInRange =
    localStorage.getItem(
      LOCAL_STORAGE_KEYS.DOCUMENT_SUMMARY_PAGE_CONSUMPTION_START_DATE_IN_RANGE,
    ) === "true";

  const summaryDocumentID = window.sessionStorage.getItem("summaryDocumentID");
  const url = new URL(window.location.href);
  const queryName = url.pathname;

  const dateFormat = getDateFormatset("/");

  // ---- API Call's
  const { sortBy, sortDirection } = useTableSorting(
    UTILITY_BILLS_TABLE.COLUMN_TO_SORT_COLUMN_MAP,
    UTILITY_BILLS_TABLE.INITIAL_SORTING_STATE,
  );

  const {
    pagination,
    paginatedQuery,
    onPageChange,
    onSizeChange,
    updateFromApiResponse,
    resetPage,
    uiPage,
  } = usePagination({
    localStorageKey: LOCAL_STORAGE_KEYS.DOCUMENT_SUMMARY_PAGE_PAGINATION,
    initialPage: 0,
    initialSize: 100,
  });

  const { size, page, totalRows } = pagination;

  const { deleteBulkBills } = useBulkDeleteUtilityBills();

  const { mutateAsync: getSavedFilterValue, data: savedFilterValueResponse } =
    useMutation(getSavedSearchFiltersList);

  const { mutateAsync: getSavedFilterListMutation, data: savedFilterData } =
    useMutation(getSavedSearchFilters);

  const getSavedFilterList = useCallback(() => {
    getSavedFilterListMutation();
  }, [getSavedFilterListMutation]);

  // ---- Column filters (apply on Search / Enter / arrow, like document-upload)
  const filterConfig = useMemo(() => createDocumentSummaryFilterConfig(), []);
  const tableFilters = useTableFilters<DocumentSummaryFilters>({
    config: filterConfig,
    pagination: { onPageChange, resetPage: () => onPageChange(1) },
  });
  const mrtFilterProps = tableFilters.getMRTFilterProps();
  const activeFiltersProps = tableFilters.getActiveFiltersProps();

  // ---- States
  const [showAnomalyColumn, setShowAnomalyColumn] = useState<boolean>(false);
  const [showDuplicateEntries, setShowDuplicateEntries] =
    useState<boolean>(storedShowDuplicates);
  const [isEdited, setIsEdited] = useState<boolean>(storedIsEdited);
  const [isMyTask, setIsMyTask] = useState<boolean>(storedIsMyTask);
  const [consumptionStartDateInRange, setConsumptionStartDateInRange] =
    useState<boolean>(storedConsumptionStartDateInRange);

  const [rowSelection, setRowSelection] = useState<MRT_RowSelectionState>({});
  const [globalFilter, setGlobalFilter] = useState<string>("");

  const [editFilterIndex, setEditFilterIndex] = useState<number>(-1);
  const [startDate, setStartDate] = useState<string>(
    storedStartDate || dayjs().month(0).startOf("month").format("YYYY-MM-DD"),
  );
  const [endDate, setEndDate] = useState<string>(
    storedEndDate || dayjs().format("YYYY-MM-DD"),
  );
  const [openDialog, setOpenDialog] = useState<boolean>(false);
  const [filterName, setFilterName] = useState<string>("");
  const [allowSearch, setAllowSearch] = useState<boolean>(true);
  const [currentSiteIds, setCurrentSiteIds] =
    useState<MultiSelectControlledOption[]>(storedSitesFilter);
  const [minDate, setMinDate] = useState<dayjs.Dayjs | undefined>(undefined);

  // searchFilters is state — only updated when Search is clicked
  const [searchFilters, setSearchFilters] = useState({
    startDate:
      storedStartDate || dayjs().month(0).startOf("month").format("YYYY-MM-DD"),
    endDate: storedEndDate || dayjs().format("YYYY-MM-DD"),
    siteIds: storedSitesFilter as MultiSelectControlledOption[],
  });
  const [isDownloading, setIsDownloading] = useState(false);

  const sites = useMemo(
    () =>
      sitesData
        ? sitesData.map((site: SiteDTO) => ({
            name: site.name,
            label: site.name,
            value: site.id,
          }))
        : [],
    [sitesData],
  );

  const buildQueryObject = useCallback(
    (overrides = {}) => ({
      isEdited,
      isMyTask,
      areDuplicates: showDuplicateEntries,
      consumptionStartDateInRange,
      siteIds: searchFilters.siteIds.map((site) => site.value as number),
      startDate: searchFilters.startDate,
      endDate: searchFilters.endDate,
      sortColumn: sortBy,
      direction: sortDirection,
      ...paginatedQuery,
      ...overrides,
    }),
    [
      isEdited,
      isMyTask,
      showDuplicateEntries,
      consumptionStartDateInRange,
      searchFilters,
      sortBy,
      sortDirection,
      paginatedQuery,
    ],
  );

  const queryObject = useMemo(
    () => ({
      ...buildQueryObject(),
      ...filterParamsToQuery(
        tableFilters.getQueryParams() as Record<string, unknown>,
      ),
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [buildQueryObject, tableFilters.filters],
  );

  const billsRecalculationQueryParams = useMemo(
    () =>
      pickBillsRecalculationQueryParams(queryObject as Record<string, unknown>),
    [queryObject],
  );

  const billsRecalculationDisplayFilters = useMemo(
    () =>
      buildBillsRecalculationDisplayFilters(
        billsRecalculationQueryParams,
        searchFilters.siteIds,
        sites.length,
      ),
    [billsRecalculationQueryParams, searchFilters, sites.length],
  );

  const {
    fetchBillsData,
    loading,
    loadingBills,
    utilityBillsData,
    duplicateData,
  } = useFetchBillsData({
    queryObject,
    updateFromApiResponse,
  });

  const tableData = useMemo(() => {
    const data = showDuplicateEntries
      ? duplicateData
      : utilityBillsData?.bills || [];
    return [...data];
  }, [showDuplicateEntries, duplicateData, utilityBillsData?.bills]);

  const billIds = useMemo(
    () => tableData.map((b: any) => String(b.id)),
    [tableData],
  );

  useEffect(() => {
    setBillEntriesNavigation(null);
  }, [billIds, setBillEntriesNavigation]);

  const onPageChangeHandler = (newPage: number) => {
    isUserPaginationChange.current = true;
    onPageChange(newPage);
  };

  const onPageSizeChangeHandler = (newPageSize: number) => {
    isUserPaginationChange.current = true;
    onSizeChange(newPageSize);
  };

  const handleDateChange = (
    date: string,
    dateType: "startDate" | "endDate",
  ) => {
    const newDate = dayjs(date);

    if (newDate.isValid()) {
      const formattedDate = newDate.format("YYYY-MM-DD");
      if (dateType === "startDate") {
        setStartDate(formattedDate);
        localStorage.setItem(
          LOCAL_STORAGE_KEYS.DOCUMENT_SUMMARY_PAGE_START_DATE,
          formattedDate,
        );
      } else {
        setEndDate(formattedDate);
        localStorage.setItem(
          LOCAL_STORAGE_KEYS.DOCUMENT_SUMMARY_PAGE_END_DATE,
          formattedDate,
        );
      }
    }
  };

  const handleSetSites = (values: MultiSelectControlledOption[]) => {
    setCurrentSiteIds(values);
    localStorage.setItem(
      LOCAL_STORAGE_KEYS.DOCUMENT_SUMMARY_PAGE_SITES_FILTER,
      JSON.stringify(values),
    );
  };

  const handleSearch = useCallback(() => {
    setSearchFilters({ startDate, endDate, siteIds: currentSiteIds });
    tableFilters.applyFilters(tableFilters.pendingFilters);
    onPageChange(1);
  }, [tableFilters, onPageChange, startDate, endDate, currentSiteIds]);

  // ---- Table Data and Columns (inject filter text field props so arrow button appears in each column with grouped headers)
  const handleDateFilterApply = (id: string, value: string | undefined) => {
    tableFilters.applyFilters([{ id, value }]);
  };

  const DATE_FILTER_IDS = new Set([
    "consumptionStartDate",
    "consumptionEndDate",
    "createdDate",
    "updateDate",
  ]);
  const formatFilterValue = (
    key: keyof DocumentSummaryFilters,
    value: any,
  ): string => {
    if (DATE_FILTER_IDS.has(key as string) && value) {
      const parsed = dayjs(value as string);
      if (parsed.isValid()) return parsed.format(getDateFormatset());
    }
    return String(value);
  };
  // This table isn't backed by react-query, so uploading/deleting a
  // supporting document doesn't refresh it on its own — refetch the current page explicitly.
  const refreshSupportingDocsCount = useCallback(() => {
    const billType = showDuplicateEntries
      ? "duplicate"
      : tableFilters.hasActiveFilters
      ? "filtered"
      : "utility";
    fetchBillsData({ billType });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showDuplicateEntries, tableFilters.hasActiveFilters, fetchBillsData]);

  const columnsTableDefinition = getBillEntriesColums({
    currentUrl,
    onApplyDateFilter: handleDateFilterApply,
    onSupportingDocsChange: refreshSupportingDocsCount,
  });
  const columnsWithFilterProps = useMemo(
    () =>
      injectFilterTextFieldProps(
        columnsTableDefinition,
        mrtFilterProps.muiTableHeadCellFilterTextFieldProps as any,
      ),
    [
      columnsTableDefinition,
      mrtFilterProps.muiTableHeadCellFilterTextFieldProps,
    ],
  );
  const columnsTableData = showAnomalyColumn
    ? columnsWithFilterProps
    : columnsWithFilterProps.filter((item: any) => item.id !== "anomalies");

  // Shared handler for the "fetch-triggering" toggles: each resets selection + page, persists
  // to localStorage, and refetches with the new value merged into the query.
  const createFilterToggleHandler =
    (
      setValue: (value: boolean) => void,
      localStorageKey: string,
      queryKey: string,
      getBillType?: (nextValue: boolean) => "duplicate" | "utility",
    ) =>
    async (nextValue: boolean) => {
      setRowSelection({});
      setValue(nextValue);
      localStorage.setItem(localStorageKey, String(nextValue));
      onPageChange(1);
      await fetchBillsData({
        billType: getBillType
          ? getBillType(nextValue)
          : showDuplicateEntries
          ? "duplicate"
          : "utility",
        queryOverrides: {
          ...queryObject,
          [queryKey]: nextValue,
          page: 0,
        },
      });
    };

  const onResetAll = async () => {
    const resetStartDate = dayjs()
      .month(0)
      .startOf("month")
      .format("YYYY-MM-DD");
    const resetEndDate = dayjs().format("YYYY-MM-DD");

    localStorage.setItem(
      LOCAL_STORAGE_KEYS.DOCUMENT_SUMMARY_PAGE_START_DATE,
      resetStartDate,
    );
    localStorage.setItem(
      LOCAL_STORAGE_KEYS.DOCUMENT_SUMMARY_PAGE_END_DATE,
      resetEndDate,
    );
    localStorage.setItem(
      LOCAL_STORAGE_KEYS.DOCUMENT_SUMMARY_PAGE_SITES_FILTER,
      JSON.stringify([]),
    );
    localStorage.setItem(
      LOCAL_STORAGE_KEYS.DOCUMENT_SUMMARY_PAGE_IS_EDITED,
      "false",
    );
    localStorage.setItem(
      LOCAL_STORAGE_KEYS.DOCUMENT_SUMMARY_PAGE_IS_MY_TASK,
      "false",
    );
    localStorage.setItem(
      LOCAL_STORAGE_KEYS.DOCUMENT_SUMMARY_PAGE_SHOW_DUPLICATES,
      "false",
    );
    localStorage.setItem(
      LOCAL_STORAGE_KEYS.DOCUMENT_SUMMARY_PAGE_CONSUMPTION_START_DATE_IN_RANGE,
      "false",
    );
    setShowDuplicateEntries(false);
    tableFilters.clearFilters();
    setIsEdited(false);
    setIsMyTask(false);
    setConsumptionStartDateInRange(false);
    setEditFilterIndex(-1);
    setCurrentSiteIds([]);
    setStartDate(resetStartDate);
    setEndDate(resetEndDate);
    setRowSelection({});

    setSearchFilters({
      startDate: resetStartDate,
      endDate: resetEndDate,
      siteIds: [],
    });

    isUserPaginationChange.current = false;
    onPageChange(1);

    await fetchBillsData({
      billType: "utility",
      queryOverrides: {
        startDate: resetStartDate,
        endDate: resetEndDate,
        siteIds: [],
        consumptionStartDateInRange: false,
        page: 0,
      },
    });
  };

  const handleSaveButton = () => {
    const appliedColumnFilters = objectToColumnFilters(
      tableFilters.filters,
      filterConfig.columnIdMap,
    );
    const columnData = {
      key: filterName,
      value: appliedColumnFilters,
    };

    if (editFilterIndex !== -1) {
      const filterValues = localData !== undefined ? localData : [];

      const index = editFilterIndex;
      if (index > -1) {
        filterValues.splice(index, 1);
      }

      const finalValue = [...filterValues, columnData];
      if (finalValue) {
        getSavedFilterValue({
          queryName,
          searchFilters: finalValue,
        });
      }
    } else {
      const filterValues = localData !== undefined ? localData : [];
      const finalValue = [...filterValues, columnData];

      if (finalValue) {
        getSavedFilterValue({
          queryName,
          searchFilters: finalValue,
        });
      }
    }
    setOpenDialog(false);
    setEditFilterIndex(-1);
    setFilterName("");
  };

  const localData = savedFilterData && savedFilterData[0]?.searchFilters;
  const selected = Object.keys(rowSelection);

  const onDownloadClick = () => {
    if (isDownloading) return;
    setIsDownloading(true);
    downloadFile(() => downloadUtilityBills(selected), "utility-bills.zip")
      .then(() =>
        notify.success("Selected utility bills were successfully downloaded!"),
      )
      .catch((error) =>
        notify.error(
          extractErrorMessage(
            error,
            "Failed to download selected utility bills!",
          ),
        ),
      )
      .finally(() => setIsDownloading(false));
  };

  const onDeleteClick = () => {
    deleteBulkBills({ ids: selected })
      .then(() => setRowSelection({}))
      .catch(() => {
        // Toasts for both request-level and partial-item failures are
        // already shown by useBulkDeleteUtilityBills — nothing else to do here.
      })
      .finally(() => {
        deleteDialogControls.close();
        setDefaultSelection();
        const billType = showDuplicateEntries
          ? "duplicate"
          : tableFilters.hasActiveFilters
          ? "filtered"
          : "utility";
        fetchBillsData({ billType });
      });
  };

  // ---- Use Effects
  useEffect(() => {
    getSavedFilterList();
  }, [getSavedFilterList]);

  useEffect(() => {
    if (savedFilterValueResponse) {
      getSavedFilterList();
    }
  }, [savedFilterValueResponse, getSavedFilterList]);

  useEffect(() => {
    if (tableInstanceRef?.current && summaryDocumentID) {
      const selectedIndex = utilityBillsData?.bills.findIndex(
        (row: any) => row.id === summaryDocumentID,
      );
      if (selectedIndex && selectedIndex !== -1) {
        const elementToScroll = rowVirtualizerInstanceRef.current;

        elementToScroll.scrollToIndex(selectedIndex + 10);
      }
    }
  }, [summaryDocumentID]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const start = dayjs(startDate);
    const end = dayjs(endDate);

    if (!start.isValid() || !end.isValid()) {
      setAllowSearch(false);
      return;
    }

    const dateValidation = end.isBefore(start);
    setAllowSearch(!dateValidation);
  }, [startDate, endDate]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!isUserPaginationChange.current) {
      return;
    }
    isUserPaginationChange.current = false;
    const billType = showDuplicateEntries
      ? "duplicate"
      : tableFilters.hasActiveFilters
      ? "filtered"
      : "utility";
    fetchBillsData({ billType, queryOverrides: { page } });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, size]);

  // Fetch when query (including column filters) or showDuplicateEntries changes.
  useEffect(() => {
    const billType = showDuplicateEntries
      ? "duplicate"
      : tableFilters.hasActiveFilters
      ? "filtered"
      : "utility";
    fetchBillsData({ billType });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryObject, showDuplicateEntries]);

  // Force virtualizer to recalculate when data changes
  useEffect(() => {
    if (
      !loadingBills &&
      rowVirtualizerInstanceRef.current &&
      tableData.length > 0
    ) {
      // Access the virtualizer and force it to measure rows
      const virtualizer = rowVirtualizerInstanceRef.current;

      virtualizer.measure?.();
    }
  }, [loadingBills, tableData, showDuplicateEntries]);

  useEffect(() => {
    if (availableYears && availableYears.length > 0) {
      setMinDate(dayjs(`${availableYears[0]}-01-01`));
    }
  }, [availableYears]);

  return loadingAvailableYears ? (
    <Loading />
  ) : (
    <Box
      sx={{
        pt: 1.5,
        pl: 3,
        display: "flex",
        flexDirection: "column",
        width: "100%",
        height: "100%",
      }}
    >
      <Box
        sx={{
          width: "100%",
          display: "flex",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 2,
          mb: 2,
        }}
      >
        <Stack direction="row" spacing={2}>
          <EsgDatePickerField
            width="160px"
            label={
              <InfoLabel
                title="Start Date"
                tooltipSx={{
                  maxWidth: "180px",
                  height: "fit-content",
                  fontSize: "10px",
                  textAlign: "center",
                }}
                labelSx={{
                  width: "100%",
                  height: "18px",
                  padding: "0px",
                  marginBottom: "4px",
                  flexDirection: "row-reverse",
                  justifyContent: "flex-end",
                  backgroundColor: "transparent",
                  "& > svg": {
                    width: "14px",
                    height: "14px",
                    color: "black",
                  },
                }}
                titleSx={{
                  color: "black",
                  fontSize: "12px",
                }}
                infoMessage="Filter to bills where any part of the bill period overlaps with the selected date range"
              />
            }
            views={["year", "month", "day"]}
            openTo="year"
            format={dateFormat}
            disableHighlightToday
            onAccept={(e: any) => handleDateChange(e, "startDate")}
            onChange={(e: any) => handleDateChange(e, "startDate")}
            setDate={(e: any) => handleDateChange(e, "startDate")}
            value={dayjs(startDate)}
            placeholdervalue={dateFormat}
            filterAll={false}
            clearDate={false}
            minDate={minDate}
            maxDate={dayjs()}
          />
          <EsgDatePickerField
            width="160px"
            label={
              <InfoLabel
                title="End Date"
                tooltipSx={{
                  maxWidth: "180px",
                  height: "fit-content",
                  fontSize: "10px",
                  textAlign: "center",
                }}
                labelSx={{
                  width: "100%",
                  height: "18px",
                  padding: "0px",
                  marginBottom: "4px",
                  flexDirection: "row-reverse",
                  justifyContent: "flex-end",
                  backgroundColor: "transparent",
                  "& > svg": {
                    width: "14px",
                    height: "14px",
                    color: "black",
                  },
                }}
                titleSx={{
                  color: "black",
                  fontSize: "12px",
                }}
                infoMessage="Filter to bills where any part of the bill period overlaps with the selected date range"
              />
            }
            views={["year", "month", "day"]}
            openTo="year"
            format={dateFormat}
            disableHighlightToday
            onAccept={(e: any) => handleDateChange(e, "endDate")}
            onChange={(e: any) => handleDateChange(e, "endDate")}
            setDate={(e: any) => handleDateChange(e, "endDate")}
            value={dayjs(endDate)}
            placeholdervalue={dateFormat}
            filterAll={false}
            clearDate={false}
            minDate={minDate}
            maxDate={dayjs()}
          />
        </Stack>
        <Box sx={{ width: "180px" }}>
          <EsgMultiselectControlledField
            label="Sites"
            selectedValues={currentSiteIds}
            setSelectedValues={handleSetSites}
            options={sites as MultiSelectControlledOption[]}
            type="checkMissing"
            isAllSelectedOnEmptySelection
          />
        </Box>
        <Box
          sx={{
            width: "296px !important",
            display: "flex",
            gap: "16px",
            mt: "22px",
          }}
        >
          <Button
            sx={{
              width: "140px",
              height: "36px",
              borderRadius: "8px",
            }}
            disabled={!allowSearch}
            variant="contained"
            onClick={() => handleSearch()}
          >
            Search
          </Button>
          <Button
            sx={{
              width: "140px",
              height: "36px",
              borderRadius: "8px",
            }}
            variant="outlined"
            onClick={onResetAll}
          >
            Clear Filters
          </Button>
        </Box>
      </Box>
      <Box
        sx={{
          width: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 2,
          pr: 3,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <Switch
            isChecked={isEdited}
            label="Show only edited"
            onChange={createFilterToggleHandler(
              setIsEdited,
              LOCAL_STORAGE_KEYS.DOCUMENT_SUMMARY_PAGE_IS_EDITED,
              "isEdited",
            )}
          />
          <Switch
            isChecked={isMyTask}
            label="Show my tasks"
            onChange={createFilterToggleHandler(
              setIsMyTask,
              LOCAL_STORAGE_KEYS.DOCUMENT_SUMMARY_PAGE_IS_MY_TASK,
              "isMyTask",
            )}
          />
          <Switch
            isChecked={showAnomalyColumn}
            label="Show anomaly column"
            onChange={setShowAnomalyColumn}
          />
          <Tooltip title="This toggle will show all cases where the account #, meter #, start date, end date, usage, and cost are the same">
            <div>
              <Switch
                isChecked={showDuplicateEntries}
                label="Show duplicate entries"
                onChange={createFilterToggleHandler(
                  setShowDuplicateEntries,
                  LOCAL_STORAGE_KEYS.DOCUMENT_SUMMARY_PAGE_SHOW_DUPLICATES,
                  "areDuplicates",
                  (nextValue) => (nextValue ? "duplicate" : "utility"),
                )}
              />
            </div>
          </Tooltip>
          {userRole.isTraceraProduct && (
            <Switch
              isChecked={consumptionStartDateInRange}
              label="Consumption start date in range"
              onChange={createFilterToggleHandler(
                setConsumptionStartDateInRange,
                LOCAL_STORAGE_KEYS.DOCUMENT_SUMMARY_PAGE_CONSUMPTION_START_DATE_IN_RANGE,
                "consumptionStartDateInRange",
              )}
            />
          )}
        </Box>
        <Stack direction="row" spacing={1} alignItems="center">
          {userRole.isTraceraProduct && (
            <BillsRecalculationButton
              displayFilters={billsRecalculationDisplayFilters}
              queryParams={billsRecalculationQueryParams}
            />
          )}
          <ExportButtonWrapper
            tableData={tableData}
            queryParams={queryObject}
            startDate={searchFilters.startDate}
            endDate={searchFilters.endDate}
            onFilterChange={resetPage}
          />
        </Stack>
      </Box>
      {loadingBills ? (
        <Loading />
      ) : (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            height: "100%",
            width: "100%",
            "& > div:nth-of-type(2)": {
              flexGrow: 1,
              pr: 3,
            },
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              width: "100%",
              marginTop: "20px",
            }}
          >
            <ActiveFilters
              appliedFilters={activeFiltersProps.appliedFilters}
              onClearFilter={(filterId) =>
                activeFiltersProps.onClearFilter(filterId)
              }
              displayNameMap={activeFiltersProps.displayNameMap}
              valueFormatter={formatFilterValue}
            />
          </Box>
          <CustomMaterialTable
            tableInstanceRef={tableInstanceRef}
            rowVirtualizerInstanceRef={rowVirtualizerInstanceRef}
            enableStickyHeader
            enableColumnResizing
            data={tableData}
            columns={columnsTableData}
            enableRowSelection
            enablePagination
            manualPagination
            rowCount={totalRows}
            onRowSelectionChange={setRowSelection}
            enableColumnFilters={mrtFilterProps.enableColumnFilters}
            manualFiltering={mrtFilterProps.manualFiltering}
            onColumnFiltersChange={mrtFilterProps.onColumnFiltersChange}
            muiTableHeadCellFilterTextFieldProps={
              mrtFilterProps.muiTableHeadCellFilterTextFieldProps
            }
            initialState={{
              density: "compact",
              columnFilters: mrtFilterProps.initialState.columnFilters,
            }}
            state={{ rowSelection, globalFilter, isLoading: loading }}
            onGlobalFilterChange={setGlobalFilter}
            getRowId={(row: any) => row?.id}
            muiTableBodyRowProps={(row: any) =>
              getMuiTableBodyRowProps(
                row,
                saveCurrentUrl,
                currentUrl,
                navigate,
                summaryDocumentID,
                billIds,
                totalRows,
                setBillEntriesNavigation,
                {
                  query: queryObject,
                  page: pagination.page,
                  size: pagination.size,
                  pageOffset: pagination.page * pagination.size,
                },
              )
            }
            renderTopToolbar={({ table }) => {
              const selectedCount = table.getSelectedRowModel().flatRows.length;
              if (!selectedCount) return null;

              return (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    pr: 2,
                    alignItems: "center",
                    backgroundColor: "#e5f6fd",
                    width: "100%",
                    gap: 2,
                    mb: 2,
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                    <ToolBarAlertBanner table={table} stackAlertBanner />
                  </Box>
                  <Box sx={{ display: "flex", gap: 1 }}>
                    <Button
                      startIcon={<EsgDownloadIcon />}
                      variant="contained"
                      sx={{ borderRadius: "8px", width: "140px" }}
                      disabled={selected?.length === 0 || isDownloading}
                      onClick={onDownloadClick}
                    >
                      {isDownloading ? "Downloading..." : "Download"}
                    </Button>
                    <Button
                      startIcon={<EsgDeleteIcon color="white" />}
                      variant="contained"
                      sx={{ borderRadius: "8px", width: "140px" }}
                      disabled={!selected?.length || userRole.isAuditor}
                      onClick={deleteDialogControls.open}
                      color="error"
                    >
                      Delete
                    </Button>
                  </Box>
                </Box>
              );
            }}
            containerSx={{
              height: "calc(100vh - 360px)",
              width: "calc(100vw - 150px)",
              "& thead": {
                "& tr": {
                  height: "auto",
                  "& > th": {
                    padding: "8px 4px 8px 16px !important",
                  },
                  "& > th:first-of-type": {
                    width: "48px",
                    minWidth: "48px",
                    paddingRight: "4px !important",
                  },
                },
              },
              "& tbody": {
                "& > tr": {
                  height: "48px",
                  "& > td": {
                    padding: "8px 4px 8px 16px !important",
                  },
                  "& > td:first-of-type": {
                    width: "fit-content",
                    minWidth: "fit-content",
                    paddingRight: "4px !important",
                  },
                  "& > td:nth-of-type(2)": {
                    paddingLeft: "0 !important",
                  },
                },
              },
            }}
            topToolbarSx={{
              height: Object.keys(rowSelection).length > 0 ? "auto" : "1px",
              minHeight: "1px",
              backgroundColor:
                Object.keys(rowSelection).length > 0 ? "white" : "transparent",
            }}
            muiTableHeadRowProps={{
              sx: {
                "& > th": {
                  paddingTop: 1,
                  paddingBottom: 1,
                  paddingLeft: "0px",
                  "& > div > div > div": { minWidth: "max-content" },
                  height: "auto",
                  alignItems: "center",
                  backgroundColor: "#F8FAFC",
                },
              },
            }}
          />
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              my: "10px",
            }}
          >
            {tableData?.length > 0 && (
              <Pagination
                page={uiPage}
                rowsCount={totalRows}
                rowsPerPage={size}
                rowsPerPageOptions={UTILITY_BILLS_TABLE.ROWS_PER_PAGE_OPTIONS}
                onPageChange={onPageChangeHandler}
                onRowsPerPageChange={onPageSizeChangeHandler}
              />
            )}
          </Box>
        </Box>
      )}
      {/* Save Filter Dialog */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)}>
        <DialogTitle>Save Filter</DialogTitle>
        <DialogContent>
          <DialogContentText>Provide Filter Name!</DialogContentText>
          <TextField
            autoFocus
            margin="dense"
            id="name"
            label="Filter Name"
            type="email"
            fullWidth
            variant="standard"
            value={filterName}
            onChange={(e) => setFilterName(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
          <Button onClick={handleSaveButton}>Save Filter</Button>
        </DialogActions>
      </Dialog>
      <DeleteDialog
        isOpen={deleteDialogControls.isOpen}
        onCancel={deleteDialogControls.close}
        onDeleteClick={onDeleteClick}
        selectedCount={selected?.length}
        target="utility bill"
        title="Delete utility bills?"
      />
    </Box>
  );
};
